import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote,
  Code, Link as LinkIcon, Undo2, Redo2, Pilcrow,
} from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder = "Write your post…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "tiptap-link" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tiptap-editor min-h-[360px] focus:outline-none",
        "data-testid": "rte-content",
      },
    },
  });

  // Keep external value in sync if it changes (e.g., when loading a post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) editor.commands.setContent(value, { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link")?.href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const TButton = ({ active, onClick, children, label, testId }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`p-2 rounded-md transition-colors ${
        active ? "bg-[#FF8C00]/15 text-[#FF8C00]" : "text-[#A1A1AA] hover:text-white hover:bg-white/5"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0C]" data-testid="rich-text-editor">
      <div className="flex items-center flex-wrap gap-1 px-2 py-1.5 border-b border-white/10 bg-white/3">
        <TButton testId="rte-bold" label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></TButton>
        <TButton testId="rte-italic" label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></TButton>
        <TButton testId="rte-strike" label="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="w-4 h-4" /></TButton>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <TButton testId="rte-p" label="Paragraph" active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow className="w-4 h-4" /></TButton>
        <TButton testId="rte-h2" label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></TButton>
        <TButton testId="rte-h3" label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></TButton>
        <span className="w-px h-5 bg-white/10 mx-1" />
        <TButton testId="rte-ul" label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></TButton>
        <TButton testId="rte-ol" label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></TButton>
        <TButton testId="rte-quote" label="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" /></TButton>
        <TButton testId="rte-code" label="Code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code className="w-4 h-4" /></TButton>
        <TButton testId="rte-link" label="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon className="w-4 h-4" /></TButton>
        <span className="ml-auto flex items-center gap-1">
          <TButton testId="rte-undo" label="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></TButton>
          <TButton testId="rte-redo" label="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></TButton>
        </span>
      </div>
      <div className="px-5 py-5">
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .tiptap-editor { color: #E4E4E7; font-family: 'Lato', system-ui, sans-serif; font-size: 16px; line-height: 1.7; }
        .tiptap-editor p.is-editor-empty:first-child::before {
          content: attr(data-placeholder); color: #555; pointer-events: none; height: 0; float: left;
        }
        .tiptap-editor h2 { font-family: 'Urbanist', sans-serif; font-weight: 900; font-size: 24px; color: #fff; margin: 1.4em 0 0.4em; }
        .tiptap-editor h3 { font-family: 'Urbanist', sans-serif; font-weight: 800; font-size: 19px; color: #fff; margin: 1.2em 0 0.3em; }
        .tiptap-editor p { margin: 0.9em 0; }
        .tiptap-editor ul, .tiptap-editor ol { padding-left: 1.4em; }
        .tiptap-editor ul { list-style: disc; }
        .tiptap-editor ol { list-style: decimal; }
        .tiptap-editor blockquote { border-left: 3px solid #FF8C00; padding: 0.3em 1em; color: #D4D4D8; font-style: italic; margin: 1.2em 0; background: rgba(255,140,0,0.05); border-radius: 0 6px 6px 0; }
        .tiptap-editor a, .tiptap-editor .tiptap-link { color: #FF8C00; text-decoration: underline; }
        .tiptap-editor code { font-family: 'JetBrains Mono', monospace; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 2px 6px; font-size: 0.92em; }
      `}</style>
    </div>
  );
}
